"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { GalleryVerticalEnd, Pencil, Plus, UserPlus, Users } from "lucide-react";

import { DeskCover, DeskEmpty, DeskMore } from "@/features/home-v2/desk/Desk";
import { GalleryTile, type TileGallery } from "@/features/home-v2/tiles";
import { Loading } from "@/features/home-v2/ui";
import { useScrollReveal } from "@/features/home-v2/useScrollReveal";
import { useAuthStore } from "@/store/useAuthStore";
import { getGalleries } from "../../api";
import GalleryInviteDialog from "../single/GalleryInviteDialog";
import "../single/gallery-dialogs.css";
import "./my-galleries.css";
import { useGridColumns } from "@/hooks/useGridColumns";

/* eslint-disable @typescript-eslint/no-explicit-any -- the galleries payload is untyped, as it was in MyGalleriesClient */

type OwnGallery = TileGallery & { follow?: { count?: number } | null };

/**
 * «گالری‌های من» — the owner's galleries inside the profile's owner tab, in
 * v2. MyGalleriesClient's fetch, unchanged: `/galleries?user_id=<me>&limit=100`
 * once the viewer is known, a failure reading as an empty shelf. Each gallery
 * is the catalogue's own tile, hung over a small owner's plate: its
 * followers, the invitation (the v2 dialog over the same `useGalleryInvite`)
 * and the edit page. Twelve first, then more as the owner scrolls.
 */
export default function MyGalleries({ lang }: { lang: "fa" | "en" }) {
  const isFa = lang === "fa";
  const t = (fa: string, en: string) => (isFa ? fa : en);
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const { user } = useAuthStore();
  const [galleries, setGalleries] = useState<OwnGallery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteId, setInviteId] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        setIsLoading(true);
        const res = await getGalleries({ user_id: Number(user.id), limit: 100 });
        const list = (res as any)?.data?.galleries ?? (res as any)?.galleries ?? [];
        setGalleries(Array.isArray(list) ? list : []);
      } catch {
        setGalleries([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user?.id]);

  const grid = useGridColumns();
  const reveal = useScrollReveal({ columns: grid.columns, loaded: galleries.length });
  const held = galleries.reduce((n, g) => n + (g.models?.nfts?.length ?? 0) + (g.models?.collections?.length ?? 0), 0);
  const followers = galleries.reduce((n, g) => n + Number(g.follow?.count ?? 0), 0);
  const create = { href: `/${lang}/galleries/create`, label: t("ساخت گالری تازه", "New gallery") };

  return (
    <div className="dk dk--tab mg">
      <DeskCover
        title={t("گالری‌های من", "My galleries")}
        lead={t("گالری‌هایی که ساخته‌اید؛ از همین‌جا دعوت بفرستید یا ویرایششان کنید.", "The galleries you've made — invite people or edit them from here.")}
        create={create}
        tally={isLoading ? [] : [
          { key: "galleries", label: t("گالری", "Galleries"), value: nf.format(galleries.length), tone: "accent" },
          { key: "held", label: t("اثر به نمایش", "Works on show"), value: nf.format(held), tone: "gold" },
          { key: "followers", label: t("دنبال‌کننده", "Followers"), value: nf.format(followers), tone: "live" },
        ]}
      />

      {isLoading ? <Loading label={t("در حال بارگذاری گالری‌ها", "Loading your galleries")} />
        : galleries.length === 0 ? <DeskEmpty icon={<GalleryVerticalEnd />} title={t("هنوز گالری‌ای نساخته‌اید", "You haven't created a gallery yet")} body={t("آثار خودتان یا دیگران را در یک گالری کنار هم بچینید و دیگران را برای همراهی دعوت کنید.", "Hang your own work, or others', together in a gallery — and invite people to join you.")} actions={<><Link className="v2-btn v2-focus" href={create.href}><Plus aria-hidden />{create.label}</Link><Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/galleries`}>{t("گشتی در گالری‌ها", "Browse galleries")}</Link></>} />
        : (
          <>
            <ul ref={grid.ref} className="mg-grid">
              {galleries.slice(0, reveal.count).map((g) => (
                <li key={g.id} className="mg-item">
                  <GalleryTile g={g} lang={lang} sizes="(max-width: 639px) 25vw, 12vw" />
                  <div className="mg-plate">
                    <span className="mg-follow" title={t("دنبال‌کننده", "Followers")}><Users aria-hidden /><bdi>{nf.format(Number(g.follow?.count ?? 0))}</bdi></span>
                    <button type="button" className="v2-btn v2-btn--ghost v2-btn--sm v2-focus" onClick={() => setInviteId(Number(g.id))}><UserPlus aria-hidden />{t("دعوت", "Invite")}</button>
                    <Link href={`/${lang}/galleries/${g.slug}/edit`} className="v2-btn v2-btn--ghost v2-btn--sm v2-focus"><Pencil aria-hidden />{t("ویرایش", "Edit")}</Link>
                  </div>
                </li>
              ))}
            </ul>
            <DeskMore show={reveal.hasMore} busy={false} onMore={reveal.more} sentinel={reveal.sentinel} label={t("گالری‌های بیشتر", "More galleries")} />
          </>
        )}

      {inviteId !== null ? <GalleryInviteDialog open onClose={() => setInviteId(null)} galleryId={inviteId} lang={lang} /> : null}
    </div>
  );
}
